import React, { useState, useRef, useEffect, useMemo } from "react";
import { Search, Loader2, Building2, ChevronDown, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Marketplace Developer / Project facet filter (Discovery Phase 1).
 * - Options are inventory-represented only (/public/developers, /public/projects),
 *   avoiding large lists of guaranteed zero-result choices.
 * - Projects narrow to the selected Developer.
 * - value: { id: string|null, name: string|null }; selecting "Any" clears.
 */
export const MarketFacetPicker = ({ kind, listingType, developerId, value, onChange, testid }) => {
  const isDev = kind === "developer";
  const path = isDev ? "/public/developers" : "/public/projects";
  const anyLabel = isDev ? "Any developer" : "Any project / development";
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    let alive = true;
    // Project facet is developer-scoped: don't fetch (and don't look broken) until a developer is chosen.
    if (!isDev && !developerId) { setOptions([]); setLoading(false); return; }
    setLoading(true);
    const params = {};
    if (listingType) params.listing_type = listingType;
    if (!isDev && developerId) params.developer_id = developerId;
    api.get(path, { params })
      .then(({ data }) => { if (alive) setOptions(data || []); })
      .catch(() => { if (alive) setOptions([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [path, listingType, developerId, isDev]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.name.toLowerCase().includes(s)) : options;
  }, [q, options]);

  const pick = (rec) => { onChange(rec); setOpen(false); setQ(""); };
  const awaitingDev = !isDev && !developerId;          // project facet, no developer selected yet
  const empty = !loading && options.length === 0;
  const disabled = (awaitingDev || empty) && !value?.name;

  // Placeholder communicates intent instead of looking broken.
  const placeholder = value?.name
    ? value.name
    : awaitingDev
      ? "Select a developer to see projects"
      : empty
        ? (isDev ? "No developers in current results" : "No projects available for this developer")
        : anyLabel;

  return (
    <div ref={boxRef} className="relative" data-testid={testid}>
      <button
        type="button"
        data-testid={`${testid}-trigger`}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="w-full h-11 mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-left text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2C59]/25 disabled:bg-slate-50 disabled:text-slate-400"
      >
        <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
        <span className={`truncate ${value?.name ? "text-slate-800 font-medium" : "text-slate-400"}`}>
          {placeholder}
        </span>
        {value?.name ? (
          <span
            data-testid={`${testid}-clear`}
            onClick={(e) => { e.stopPropagation(); pick({ id: null, name: null }); }}
            className="ml-auto text-slate-400 hover:text-slate-700"
          ><X className="h-4 w-4" /></span>
        ) : (
          loading ? <Loader2 className="ml-auto h-4 w-4 animate-spin text-slate-400" /> : <ChevronDown className="ml-auto h-4 w-4 text-slate-400" />
        )}
      </button>

      {open && !empty && (
        <div className="absolute z-30 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg" data-testid={`${testid}-panel`}>
          <div className="relative p-2 border-b border-slate-100">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              autoFocus
              data-testid={`${testid}-search`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={isDev ? "Search developer…" : "Search project…"}
              className="w-full h-9 pl-8 pr-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2C59]/20"
            />
          </div>
          <ul className="max-h-64 overflow-auto py-1" data-testid={`${testid}-results`}>
            <li>
              <button type="button" data-testid={`${testid}-any`} onClick={() => pick({ id: null, name: null })}
                className="w-full px-3 py-2.5 text-left text-sm text-slate-500 hover:bg-slate-50">{anyLabel}</button>
            </li>
            {filtered.map((o) => (
              <li key={o.id}>
                <button type="button" data-testid={`${testid}-option-${o.id}`} onClick={() => pick({ id: o.id, name: o.name })}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50 ${value?.id === o.id ? "bg-slate-50 font-semibold text-[#0F2C59]" : ""}`}>
                  <Building2 className="h-4 w-4 text-slate-400 shrink-0" /><span className="truncate">{o.name}</span>
                </button>
              </li>
            ))}
            {!filtered.length && <li className="px-3 py-2.5 text-xs text-slate-400">No matches.</li>}
          </ul>
        </div>
      )}
    </div>
  );
};
